import React, { useCallback, useContext, useEffect, useState } from 'react';

import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import { SECONDARY_FLOW_PARENT_ROUTES } from '../../../../constants/auth';
import { DEFAULT_DATA_ORIGIN } from '../../../../constants/constants';
import { PERMISSIONS_BY_ROUTE } from '../../../../constants/permissions';
import { CLICK_ACTION, SAVE_CHANGES, SCREEN_HOME, SECTION_ORGANIZATION } from '../../../../constants/tagManager';
import { CompanyContext } from '../../../../context/company/companyContext';
import usePermissions from '../../../../hooks/usePermissions/usePermissions';
import { IUserCompany, IUserHolding } from '../../../../interfaces/businessData';
import TagManagerService from '../../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import BdsButton from '../../Buttons/BdsButton/BdsButton';
import BdsDivider from '../BdsDivider/BdsDivider';
import ChangeCompanyWarningModal from '../ChangeCompanyWarningModal/ChangeCompanyWarningModal';
import GenericSideBar from '../GenericSideBar/GenericSideBar';

import './CompanySelectorPanel.scss';

interface ICompanyHoldingDict {
  [companyId: string]: {
    company: IUserCompany;
    holding: IUserHolding;
  };
}

interface CompanySelectorPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompanySelectorPanel = ({ isOpen, onClose }: CompanySelectorPanelProps) => {
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [companyHoldingDict, setCompanyHoldingDict] = useState<ICompanyHoldingDict>();
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const { company, holdingList, setUserCompanyAndHolding, setUserDataOrigin } = useContext(CompanyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { companyRole, hasAnyAccess } = usePermissions();
  const { hide } = useIntercom();

  const { t } = useTranslation();

  const parentRoute = SECONDARY_FLOW_PARENT_ROUTES[location.pathname];

  const changeCompany = useCallback(() => {
    if (!companyHoldingDict) return;
    const selectedCompanyHolding = companyHoldingDict[selectedCompany];
    const companyData = selectedCompanyHolding.company;
    const holdingData = selectedCompanyHolding.holding;
    const targetRoute = parentRoute ?? location.pathname;

    if (!hasAnyAccess(PERMISSIONS_BY_ROUTE[targetRoute], companyData)) {
      void navigate(companyRole.defaultRoute);
    } else if (parentRoute) {
      void navigate(parentRoute);
    }
    hide();
    setUserCompanyAndHolding(companyData, holdingData);
    setUserDataOrigin(DEFAULT_DATA_ORIGIN);
    TagManagerService.pushEvent(CLICK_ACTION, SAVE_CHANGES, { ...SECTION_ORGANIZATION, ...SCREEN_HOME });
    onClose();
  }, [
    navigate,
    companyRole.defaultRoute,
    setUserCompanyAndHolding,
    hasAnyAccess,
    setUserDataOrigin,
    hide,
    selectedCompany,
    companyHoldingDict,
    location.pathname,
    parentRoute,
    onClose
  ]);

  const handleCompanyMenu = () => {
    if (!parentRoute) {
      changeCompany();
    } else if (selectedCompany !== company.id) {
      setOpenWarningModal(true);
    } else {
      onClose();
    }
  };

  const handleConfirmWarning = () => {
    setOpenWarningModal(false);
    changeCompany();
  };

  const handleCancelWarning = () => {
    setOpenWarningModal(false);
    setSelectedCompany(company.id);
    onClose();
  };

  const handleClosePanel = useCallback(() => {
    if (openWarningModal) return;
    setSelectedCompany(company.id);
    onClose();
  }, [openWarningModal, company.id, onClose]);

  const handleSelectCompany = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedCompany(value);
  };

  useEffect(() => {
    const companies: ICompanyHoldingDict = {};

    holdingList.forEach((holding) => {
      holding.companies.forEach((company) => {
        companies[company.id] = {
          company: company,
          holding: holding
        };
      });
    });
    setCompanyHoldingDict(companies);
    if (company.id) {
      setSelectedCompany(company.id);
    }
  }, [holdingList, company.id]);

  return (
    <GenericSideBar isOpen={isOpen} onClose={handleClosePanel} position="left">
      <div className="company-sidebar">
        <div className="company-sidebar-title">{t(TRANSLATIONS_GLOBAL.header.companySelector.title)}</div>
        <div className="company-sidebar-container">
          <FormControl variant="standard" className="w-full">
            <RadioGroup
              row
              value={selectedCompany}
              className="flex flex-col"
              name="radio-buttons-group"
              role="company-selector"
              onChange={handleSelectCompany}
            >
              <div className="company-sidebar-holding-container">
                {holdingList.map((holdingData) => (
                  <div key={holdingData.id}>
                    <div key={holdingData.name} className="company-sidebar-holding">
                      {holdingData.name}
                    </div>

                    {holdingData.companies.map((companyData) => (
                      <React.Fragment key={companyData.id}>
                        <FormControlLabel
                          value={companyData.id}
                          className={`company-sidebar-selector ${companyData.id === selectedCompany ? 'selected' : ''}`}
                          control={<Radio className="company-sidebar-circle" />}
                          label={companyData.name}
                          labelPlacement="start"
                        />
                        <BdsDivider color="subtle" size="s" />
                      </React.Fragment>
                    ))}
                  </div>
                ))}
              </div>
            </RadioGroup>
          </FormControl>
        </div>
        <BdsButton action={handleCompanyMenu} disabled={!selectedCompany} variant="primary" size="s">
          {t(TRANSLATIONS_GLOBAL.header.companySelector.button)}
        </BdsButton>
      </div>
      <ChangeCompanyWarningModal
        open={openWarningModal}
        companyName={companyHoldingDict?.[selectedCompany]?.company.name ?? ''}
        onConfirm={handleConfirmWarning}
        onCancel={handleCancelWarning}
      />
    </GenericSideBar>
  );
};

export default CompanySelectorPanel;
